import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkle, PaperPlaneTilt, Warning } from "@phosphor-icons/react";

export default function AIAssistant({ recipeContext }) {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    { id: "intro", role: "ai", text: "Hi. I'm your FDA compliance co-pilot. Paste a claim, ask about allergen rules, or ask me to review your current formulation." },
  ]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    const userId = `u-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setMessages((m) => [...m, { id: userId, role: "user", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", {
        message: msg,
        session_id: sessionId,
        recipe_context: recipeContext || null,
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { id: `ai-${Date.now()}`, role: "ai", text: data.reply }]);
    } catch (err) {
      console.error("AI chat request failed", err);
      // Surface specific server messages so the demo visitor doesn't get a
      // generic "AI down" when they're actually just blocked from the AI.
      const detail = err?.response?.data?.detail;
      let text = "AI service unavailable. Try again shortly.";
      if (detail && typeof detail === "object") {
        if (detail.error === "demo_no_access") {
          text = "AI Compliance Assistant is not available in the demo. Sign in to ask FDA compliance questions.";
        } else if (detail.message) {
          text = detail.message;
        }
      } else if (typeof detail === "string") {
        text = detail;
      }
      setMessages((m) => [...m, { id: `err-${Date.now()}`, role: "error", text }]);
    } finally {
      setLoading(false);
    }
  };

  const quick = [
    "Review my current formulation for allergen compliance",
    "What claims can I make based on this nutrition profile?",
    "Is this ingredient statement properly ordered?",
  ];

  return (
    <div className="rounded-lg border border-brand-200 bg-gradient-to-br from-brand-50/50 to-stone-50 p-4"
      data-testid="ai-assistant">
      <div className="flex items-center gap-2 mb-3">
        <div className="h-8 w-8 rounded-md bg-brand-800 grid place-items-center text-white pulse-beam">
          <Sparkle size={18} weight="fill" />
        </div>
        <div>
          <div className="font-semibold text-stone-900">Compliance Assistant</div>
          <div className="text-xs text-stone-500">Powered by Claude · Reviews 21 CFR 101 compliance</div>
        </div>
      </div>

      <div className="max-h-80 overflow-y-auto space-y-2 mb-3 pr-1">
        {messages.map((m) => (
          <div key={m.id} className={`rounded-md px-3 py-2 text-sm whitespace-pre-wrap ${
            m.role === "user" ? "bg-brand-800 text-white ml-8"
            : m.role === "error" ? "bg-red-50 text-red-900 border border-red-200"
            : "bg-white border border-stone-200 mr-8"
          }`}>
            {m.role === "error" && <Warning className="inline mr-1" size={14} weight="bold" />}
            {m.text}
          </div>
        ))}
        {loading && <div className="text-xs text-stone-500 italic">Analyzing…</div>}
      </div>

      <div className="flex flex-wrap gap-1 mb-2">
        {quick.map((q) => (
          <button key={q} onClick={() => setInput(q)} data-testid={`quick-${q.slice(0,10)}`}
            className="text-[11px] px-2 py-1 rounded-full bg-white border border-stone-200 hover:border-brand-400 text-stone-600">
            {q}
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <Textarea value={input} onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about FDA compliance, claims, allergens…"
          className="min-h-[60px] border-stone-300 focus-visible:ring-brand-500"
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
          data-testid="ai-input" />
        <Button onClick={send} disabled={loading} className="bg-brand-800 hover:bg-brand-700 text-white self-end"
          data-testid="ai-send-button">
          <PaperPlaneTilt size={16} weight="bold" />
        </Button>
      </div>
      <div className="text-[10px] text-stone-500 mt-2 leading-snug" data-testid="ai-legal-disclaimer">
        <b>Informational guidance only — not legal advice.</b> Verify with a qualified food-law attorney before commercialization.
      </div>
    </div>
  );
}
